/**
 * Utility functions for iframe embedding support
 */

/**
 * Checks if the current page is running inside an iframe
 */
export const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // If we can't access window.top due to cross-origin restrictions,
    // we're definitely in an iframe
    return true;
  }
};

/**
 * Gets the URL parameter value to check if iframe mode is explicitly enabled
 */
export const isIframeMode = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('iframe') === 'true' || isInIframe();
};

/**
 * Post a message to the parent window (if in iframe)
 */
export const postMessageToParent = (message: IframeMessage) => {
  if (isInIframe() && window.parent) {
    window.parent.postMessage(message, '*');
  }
};

/**
 * Message types that can be sent from iframe to parent
 */
export interface IframeMessage {
  type: 'JOB_BOARD_READY' | 'JOB_BOARD_HEIGHT_CHANGE' | 'JOB_SELECTED' | 'APPLICATION_SUBMITTED';
  payload?: {
    height?: number;
    jobId?: string;
    success?: boolean;
    [key: string]: unknown;
  };
}

/**
 * Hook to communicate height changes to parent window
 */
export const useIframeHeightSync = () => {
  const syncHeight = () => {
    if (!isIframeMode()) return;

    const height = document.documentElement.scrollHeight;
    postMessageToParent({
      type: 'JOB_BOARD_HEIGHT_CHANGE',
      payload: { height }
    });
  };

  return syncHeight;
};

/**
 * Notify parent that iframe is ready
 */
export const notifyIframeReady = () => {
  postMessageToParent({
    type: 'JOB_BOARD_READY'
  });
};

/**
 * Notify parent when a job is selected
 */
export const notifyJobSelected = (jobId: string) => {
  postMessageToParent({
    type: 'JOB_SELECTED',
    payload: { jobId }
  });
};

/**
 * Notify parent when application is submitted
 */
export const notifyApplicationSubmitted = (success: boolean) => {
  postMessageToParent({
    type: 'APPLICATION_SUBMITTED',
    payload: { success }
  });
};
